import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Container,
    Stack,
    Divider,
    Link as MuiLink,
    Chip,
    Collapse,
    IconButton,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import {
    CheckCircle as CheckIcon,
    Bolt as BoltIcon,
    AutoAwesome as AutoIcon,
    Insights as InsightsIcon,
    Reply as ReplyIcon,
    ArrowForward as ArrowForwardIcon,
    Title as TitleIcon,
    Dashboard as DashboardIcon,
    Instagram as InstagramIcon,
    YouTube as YouTubeIcon,
    Facebook as FacebookIcon,
    PlayCircle as PlayIcon,
    Tune as TuneIcon,
    BarChart as BarChartIcon,
    Group as GroupIcon,
} from '@mui/icons-material';
import PricingSection from './PricingSection';
import NewsletterSection from './NewsletterSection';
import { featuresApi } from '../../services/api';

/* ─────────────────────────────────────────────────────────────────────────────
   REUSABLE ZIGZAG SECTION
   Text + image always side-by-side. `reverse` flips which side image goes on.
───────────────────────────────────────────────────────────────────────────── */
const ZigzagSection = ({ tag, tagColor = '#00d2ff', icon, title, description, points, imageSrc, reverse = false }) => {
    const accent = tagColor;
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: reverse ? 'row-reverse' : 'row' },
            alignItems: 'center',
            gap: { xs: 4, md: 7 },
            mb: { xs: 8, md: 10 },
        }}>
            {/* TEXT */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                    <Box sx={{
                        width: 36, height: 36, borderRadius: 2,
                        bgcolor: `${accent}15`, border: `1px solid ${accent}40`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        {icon}
                    </Box>
                    <Typography variant="overline" sx={{ fontWeight: 800, color: accent, letterSpacing: '0.18em', fontSize: '0.7rem' }}>
                        {tag}
                    </Typography>
                </Box>

                <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '3.2rem' }, lineHeight: 1.1, letterSpacing: '-0.03em', mb: 2.5 }}>
                    {title}
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.8, fontSize: '1.05rem', mb: 3, maxWidth: 480 }}>
                    {description}
                </Typography>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', mb: 3 }} />

                <Stack spacing={1.5} sx={{ mb: 3.5 }}>
                    {points.map((pt, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CheckIcon sx={{ color: accent, fontSize: 18, flexShrink: 0 }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.75)' }}>{pt}</Typography>
                        </Box>
                    ))}
                </Stack>

                <Button endIcon={<ArrowForwardIcon />} sx={{ color: accent, fontWeight: 800, fontSize: '1rem', textTransform: 'none', px: 0, '&:hover': { bgcolor: 'transparent', opacity: 0.8 } }}>
                    Learn how it works
                </Button>
            </Box>

            {/* IMAGE */}
            <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
                <Box sx={{ position: 'absolute', inset: '-30%', background: `radial-gradient(circle, ${accent}18 0%, transparent 70%)`, zIndex: 0, pointerEvents: 'none' }} />
                <Box sx={{
                    position: 'relative', zIndex: 1,
                    borderRadius: 5, overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.08)',
                    bgcolor: 'rgba(255,255,255,0.02)',
                    boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
                    transition: 'transform 0.5s cubic-bezier(0.2,0.8,0.2,1)',
                    '&:hover': { transform: 'translateY(-8px) scale(1.01)' },
                }}>
                    <Box component="img" src={imageSrc} alt={tag} sx={{ width: '100%', display: 'block' }}
                        onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.minHeight = '320px'; e.target.parentElement.style.background = `linear-gradient(135deg, ${accent}10 0%, rgba(255,255,255,0.02) 100%)`; }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

const DEMO_LEADS = [
    {
        name: 'Priya Sharma',
        initials: 'PS',
        avatarBg: '#e1306c',          // Instagram pink
        platform: 'Instagram',
        platformColor: '#e1306c',
        comment: '"This is exactly what I needed for my startup 🔥"',
        status: 'Hot Lead',
        statusColor: '#ff4d4d',
        statusBg: 'rgba(255,77,77,0.12)',
        time: '2 min ago',
        intent: 'High Intent',
    },
    {
        name: 'Arjun Mehta',
        initials: 'AM',
        avatarBg: '#ff0000',          // YouTube red
        platform: 'YouTube',
        platformColor: '#ff0000',
        comment: '"How do I sign up for the early access? Looks insane!"',
        status: 'Interested',
        statusColor: '#00d2ff',
        statusBg: 'rgba(0,210,255,0.12)',
        time: '8 min ago',
        intent: 'Medium Intent',
    },
    {
        name: 'Neha Patel',
        initials: 'NP',
        avatarBg: '#1877f2',          // Facebook blue
        platform: 'Facebook',
        platformColor: '#1877f2',
        comment: '"We need this for our agency. What\'s the pricing?"',
        status: 'Hot Lead',
        statusColor: '#ff4d4d',
        statusBg: 'rgba(255,77,77,0.12)',
        time: '15 min ago',
        intent: 'High Intent',
    },
];

const DemoCrmPreview = () => (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Header bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontWeight: 700, letterSpacing: '0.1em', fontSize: '0.6rem' }}>
                AI-DETECTED LEADS · 3 NEW TODAY
            </Typography>
            <Box sx={{ px: 1.5, py: 0.3, borderRadius: '100px', bgcolor: 'rgba(255,77,77,0.12)', border: '1px solid rgba(255,77,77,0.25)' }}>
                <Typography variant="caption" sx={{ color: '#ff4d4d', fontWeight: 800, fontSize: '0.6rem' }}>● LIVE</Typography>
            </Box>
        </Box>

        {/* Lead Cards */}
        {DEMO_LEADS.map((lead, i) => (
            <Box key={i} sx={{
                p: 1.5, borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.06)',
                bgcolor: 'rgba(255,255,255,0.02)',
                display: 'flex', gap: 1.5, alignItems: 'flex-start',
                transition: 'all 0.2s',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.1)' },
            }}>
                {/* Avatar */}
                <Box sx={{
                    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                    bgcolor: lead.avatarBg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: '0.75rem', color: 'white',
                    border: `2px solid ${lead.avatarBg}60`,
                }}>
                    {lead.initials}
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: 'white', fontSize: '0.78rem' }}>
                            {lead.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: lead.platformColor, fontSize: '0.65rem', fontWeight: 700 }}>
                            via {lead.platform}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.6rem', ml: 'auto', flexShrink: 0 }}>
                            {lead.time}
                        </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.72rem', display: 'block', mb: 0.8, fontStyle: 'italic' }}>
                        {lead.comment}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Box sx={{ px: 1, py: 0.2, borderRadius: '100px', bgcolor: lead.statusBg, border: `1px solid ${lead.statusColor}40` }}>
                            <Typography variant="caption" sx={{ color: lead.statusColor, fontWeight: 800, fontSize: '0.58rem' }}>
                                {lead.status}
                            </Typography>
                        </Box>
                        <Box sx={{ px: 1, py: 0.2, borderRadius: '100px', bgcolor: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.25)' }}>
                            <Typography variant="caption" sx={{ color: '#a78bfa', fontWeight: 700, fontSize: '0.58rem' }}>
                                ✦ AI: {lead.intent}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        ))}
    </Box>
);

/* ─────────────────────────────────────────────────────────────────────────────
   INLINE DEMO COMPONENT
   Tab switcher showing different feature previews
───────────────────────────────────────────────────────────────────────────── */
const DemoSection = () => {
    const [activeTab, setActiveTab] = useState(0);

    const tabs = [
        {
            label: 'Lead CRM',
            icon: <GroupIcon sx={{ fontSize: 16 }} />,
            desc: 'View and manage all AI-detected leads from every platform in one unified dashboard.',
            image: '/assets/crm_preview.png',
            accent: '#00d2ff',
            badge: 'LIVE',
        },
        {
            label: 'AI Content',
            icon: <AutoIcon sx={{ fontSize: 16 }} />,
            desc: 'One click generates SEO-optimised titles, descriptions, hooks and hashtags powered by Gemini AI.',
            image: '/assets/hero_mockup.png',
            accent: '#8b5cf6',
            badge: 'AI',
        },
        {
            label: 'Analytics',
            icon: <BarChartIcon sx={{ fontSize: 16 }} />,
            desc: 'Cross-platform performance analytics — compare reach, engagement and conversions side-by-side.',
            image: '/assets/hero_mockup.png',
            accent: '#10d98a',
            badge: 'INSIGHTS',
        },
    ];

    const t = tabs[activeTab];

    return (
        <Box id="demo" sx={{ py: { xs: 8, md: 12 }, position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            {/* Glow */}
            <Box sx={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%,-50%)', width: '70vw', height: '40vh', background: 'radial-gradient(circle, rgba(139,92,246,0.08) 0%, transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none' }} />

            <Container maxWidth="lg" sx={{ position: 'relative' }}>
                {/* Header */}
                <Box sx={{ textAlign: 'center', mb: 8 }}>
                    <Typography variant="overline" sx={{ fontWeight: 800, color: '#8b5cf6', letterSpacing: '0.18em', fontSize: '0.7rem' }}>
                        PRODUCT DEMO
                    </Typography>
                    <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '3rem' }, letterSpacing: '-0.04em', mt: 1, mb: 2 }}>
                        See Socialixer in action.
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.45)', maxWidth: 520, mx: 'auto', lineHeight: 1.8 }}>
                        A single workspace that replaces five different tools. No tabs, no context-switching, no missed leads.
                    </Typography>
                </Box>

                {/* Tab Bar */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mb: 6, flexWrap: 'wrap' }}>
                    {tabs.map((tab, i) => (
                        <Box
                            key={i}
                            onClick={() => setActiveTab(i)}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 1,
                                px: 3, py: 1.2, borderRadius: '100px', cursor: 'pointer',
                                border: activeTab === i ? `1px solid ${tab.accent}60` : '1px solid rgba(255,255,255,0.1)',
                                bgcolor: activeTab === i ? `${tab.accent}12` : 'transparent',
                                transition: 'all 0.25s',
                                '&:hover': { borderColor: `${tab.accent}50`, bgcolor: `${tab.accent}08` },
                            }}
                        >
                            <Box sx={{ color: activeTab === i ? tab.accent : 'rgba(255,255,255,0.5)', display: 'flex' }}>{tab.icon}</Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: activeTab === i ? tab.accent : 'rgba(255,255,255,0.6)' }}>
                                {tab.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Demo Window */}
                <Box sx={{
                    borderRadius: 5,
                    border: '1px solid rgba(255,255,255,0.08)',
                    bgcolor: 'rgba(255,255,255,0.015)',
                    overflow: 'hidden',
                    boxShadow: `0 40px 100px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)`,
                }}>
                    {/* Window chrome */}
                    <Box sx={{ px: 3, py: 2, borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'rgba(255,255,255,0.02)' }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {['#ff5f57', '#ffbd2e', '#28c940'].map((c) => (
                                <Box key={c} sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: c }} />
                            ))}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 0.6, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                            <BoltIcon sx={{ fontSize: 12, color: '#00d2ff' }} />
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>app.commandcenter.ai</Typography>
                        </Box>
                        <Chip label={t.badge} size="small" sx={{ bgcolor: `${t.accent}20`, color: t.accent, fontWeight: 800, fontSize: '0.6rem', letterSpacing: '0.1em', border: `1px solid ${t.accent}40` }} />
                    </Box>

                    {/* Screen content */}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: { xs: 'auto', md: 480 } }}>
                        {/* Sidebar */}
                        <Box sx={{ width: { xs: '100%', md: 220 }, borderRight: { md: '1px solid rgba(255,255,255,0.06)' }, borderBottom: { xs: '1px solid rgba(255,255,255,0.06)', md: 'none' }, p: 2, bgcolor: 'rgba(255,255,255,0.01)' }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontWeight: 700, letterSpacing: '0.12em', fontSize: '0.6rem', display: 'block', mb: 1.5 }}>WORKSPACE</Typography>
                            {[
                                { label: 'Dashboard', icon: <DashboardIcon sx={{ fontSize: 15 }} />, active: false },
                                { label: 'Lead CRM', icon: <GroupIcon sx={{ fontSize: 15 }} />, active: activeTab === 0 },
                                { label: 'AI Content', icon: <AutoIcon sx={{ fontSize: 15 }} />, active: activeTab === 1 },
                                { label: 'Analytics', icon: <BarChartIcon sx={{ fontSize: 15 }} />, active: activeTab === 2 },
                                { label: 'Settings', icon: <TuneIcon sx={{ fontSize: 15 }} />, active: false },
                            ].map((item) => (
                                <Box key={item.label} sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                    px: 1.5, py: 1, borderRadius: 2, mb: 0.5,
                                    bgcolor: item.active ? `${t.accent}15` : 'transparent',
                                    border: item.active ? `1px solid ${t.accent}30` : '1px solid transparent',
                                }}>
                                    <Box sx={{ color: item.active ? t.accent : 'rgba(255,255,255,0.35)' }}>{item.icon}</Box>
                                    <Typography variant="caption" sx={{ fontWeight: item.active ? 700 : 500, color: item.active ? t.accent : 'rgba(255,255,255,0.45)', fontSize: '0.78rem' }}>
                                        {item.label}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* Main content area */}
                        <Box sx={{ flex: 1, p: { xs: 3, md: 4 } }}>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>{t.label}</Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.7 }}>{t.desc}</Typography>
                            </Box>
                            <Box sx={{
                                borderRadius: 3, overflow: 'hidden',
                                border: '1px solid rgba(255,255,255,0.07)',
                                bgcolor: 'rgba(255,255,255,0.01)',
                                minHeight: 300,
                            }}>
                                {activeTab === 0 ? (
                                    <DemoCrmPreview />
                                ) : (
                                    <Box component="img" src={t.image} alt={t.label} sx={{ width: '100%', display: 'block' }}
                                        onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.minHeight = '300px'; e.target.parentElement.style.background = `linear-gradient(135deg, ${t.accent}08 0%, transparent 100%)`; }}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ textAlign: 'center', mt: 5 }} />
            </Container>
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────────────────────
   UNIFIED PLATFORM SECTION
   "One hub. All your platforms."
───────────────────────────────────────────────────────────────────────────── */
const UnifiedPlatformSection = () => {
    const platforms = [
        { name: 'Instagram', icon: <InstagramIcon />, color: '#e1306c', handle: '@yourbrand' },
        { name: 'YouTube', icon: <YouTubeIcon />, color: '#ff0000', handle: 'YourChannel' },
        { name: 'Facebook', icon: <FacebookIcon />, color: '#1877f2', handle: 'Your Page' },
    ];

    return (
        <Box sx={{ py: { xs: 6, md: 8 }, position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden' }}>
            {/* Background accent */}
            <Box sx={{ position: 'absolute', top: '-20%', right: '-10%', width: '50vw', height: '50vw', background: 'radial-gradient(circle, rgba(0,210,255,0.05) 0%, transparent 70%)', filter: 'blur(80px)', pointerEvents: 'none' }} />

            <Container maxWidth="lg" sx={{ position: 'relative' }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    gap: { xs: 8, md: 12 },
                }}>
                    {/* Text side */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.8, borderRadius: '100px', bgcolor: 'rgba(0,210,255,0.07)', border: '1px solid rgba(0,210,255,0.2)', mb: 3 }}>
                            <DashboardIcon sx={{ fontSize: 14, color: '#00d2ff' }} />
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#00d2ff', letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                                ALL-IN-ONE HUB
                            </Typography>
                        </Box>

                        <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2.2rem', md: '3.5rem' }, lineHeight: 1.1, letterSpacing: '-0.04em', mb: 2.5 }}>
                            One platform. {' '}
                            <Box component="span" sx={{ background: 'linear-gradient(90deg, #00d2ff, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                                All 3 platforms.
                            </Box>
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.8, fontSize: '1.05rem', mb: 4, maxWidth: 460 }}>
                            Stop switching between apps. Post to YouTube, Instagram, and Facebook simultaneously. Connect your accounts and control everything from a single powerful dashboard.
                        </Typography>

                        <Stack spacing={1.5} sx={{ mb: 4 }}>
                            {[
                                'Connect unlimited accounts per platform',
                                'Unified inbox for comments, DMs & leads',
                                'Cross-platform analytics in one view',
                                'Publish & schedule across all channels',
                            ].map((pt, i) => (
                                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <CheckIcon sx={{ color: '#00d2ff', fontSize: 18, flexShrink: 0 }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.75)' }}>{pt}</Typography>
                                </Box>
                            ))}
                        </Stack>

                    </Box>

                    {/* Visual: Platform cards */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{
                            p: 3, borderRadius: 5,
                            bgcolor: 'rgba(255,255,255,0.02)',
                            border: '1px solid rgba(255,255,255,0.07)',
                            boxShadow: '0 30px 80px rgba(0,0,0,0.4)',
                        }}>
                            {/* Header */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                <Typography variant="body2" sx={{ fontWeight: 800, color: 'rgba(255,255,255,0.7)' }}>Connected Platforms</Typography>
                                <Chip label="3 Platforms" size="small" sx={{ bgcolor: 'rgba(0,210,255,0.1)', color: '#00d2ff', fontWeight: 700, border: '1px solid rgba(0,210,255,0.3)', fontSize: '0.65rem' }} />
                            </Box>

                            <Stack spacing={1.5}>
                                {platforms.map((p) => (
                                    <Box key={p.name} sx={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        p: 2, borderRadius: 3,
                                        bgcolor: 'rgba(255,255,255,0.025)',
                                        border: '1px solid rgba(255,255,255,0.06)',
                                        transition: 'all 0.2s',
                                        '&:hover': { bgcolor: `${p.color}10`, borderColor: `${p.color}40` },
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Box sx={{ color: p.color, display: 'flex', width: 32, height: 32, borderRadius: 2, bgcolor: `${p.color}15`, border: `1px solid ${p.color}30`, alignItems: 'center', justifyContent: 'center' }}>
                                                {p.icon}
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{p.name}</Typography>
                                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>{p.handle}</Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#28c940' }} />
                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 600 }}>Live</Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>

                            <Box sx={{ mt: 3, p: 2, borderRadius: 3, bgcolor: 'rgba(0,210,255,0.05)', border: '1px solid rgba(0,210,255,0.15)', display: 'flex', alignItems: 'center', gap: 2 }}>
                                <BoltIcon sx={{ color: '#00d2ff', fontSize: 18 }} />
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>
                                    AI is actively monitoring <Box component="span" sx={{ color: '#00d2ff' }}>247 comments</Box> across all platforms
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────────────────────
   AI SEO / CAPTION SECTION
   "AI-generated titles, descriptions & hashtags"
───────────────────────────────────────────────────────────────────────────── */
const AiSeoSection = () => {
    const [activeExample, setActiveExample] = useState(0);

    const examples = [
        {
            platform: 'YouTube',
            input: 'My product tutorial video',
            title: 'How I 10x\'d My Sales Using This One AI Tool (Full Tutorial)',
            description: 'In this video, I break down the exact AI automation system that helped me convert Instagram comments into paying customers — step by step. No fluff.',
            tags: ['#AITool', '#SocialMediaGrowth', '#Automation', '#ContentCreator'],
            color: '#ff0000',
        },
        {
            platform: 'Instagram',
            input: 'Fitness transformation post',
            title: '6-Week Transformation ✨',
            description: 'The results only come when you stop waiting for motivation and start building discipline. Here\'s what 6 weeks of consistency looks like 📈 Comment "GUIDE" to get my free plan.',
            tags: ['#Transformation', '#FitnessJourney', '#Discipline', '#HealthyLifestyle'],
            color: '#e1306c',
        },
        {
            platform: 'Facebook',
            input: 'Quarterly business growth update',
            title: 'We grew 340% in Q4. Here\'s what changed.',
            description: 'A year ago, we were manually replying to every social comment. Today, AI handles 90% of it. Here are the 3 systems we implemented that completely changed our growth trajectory.',
            tags: ['#BusinessGrowth', '#AIStrategy', '#Entrepreneurship', '#B2B'],
            color: '#1877f2',
        },
    ];

    const ex = examples[activeExample];

    return (
        <Box sx={{ py: { xs: 6, md: 8 }, position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <Box sx={{ position: 'absolute', top: '30%', left: '-5%', width: '40vw', height: '40vw', background: 'radial-gradient(circle, rgba(139,92,246,0.07) 0%, transparent 70%)', filter: 'blur(80px)', pointerEvents: 'none' }} />

            <Container maxWidth="lg" sx={{ position: 'relative' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: { xs: 8, md: 12 } }}>

                    {/* Live preview card */}
                    <Box sx={{ flex: 1, minWidth: 0, order: { xs: 2, md: 1 } }}>
                        {/* Platform switcher */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                            {examples.map((e, i) => (
                                <Box key={i} onClick={() => setActiveExample(i)}
                                    sx={{
                                        px: 2.5, py: 0.8, borderRadius: '100px', cursor: 'pointer',
                                        border: activeExample === i ? `1px solid ${e.color}60` : '1px solid rgba(255,255,255,0.1)',
                                        bgcolor: activeExample === i ? `${e.color}12` : 'transparent',
                                        transition: 'all 0.2s',
                                    }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: activeExample === i ? e.color : 'rgba(255,255,255,0.5)', fontSize: '0.75rem' }}>
                                        {e.platform}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        <Box sx={{ p: 3, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', boxShadow: '0 30px 80px rgba(0,0,0,0.4)' }}>
                            {/* Input */}
                            <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', mb: 3 }}>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontWeight: 600, display: 'block', mb: 0.5, fontSize: '0.65rem', letterSpacing: '0.1em' }}>YOUR INPUT</Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>{ex.input}</Typography>
                            </Box>

                            {/* Generated title */}
                            <Box sx={{ mb: 2.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <TitleIcon sx={{ color: '#8b5cf6', fontSize: 14 }} />
                                    <Typography variant="caption" sx={{ color: '#8b5cf6', fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.1em' }}>AI TITLE</Typography>
                                </Box>
                                <Typography variant="body1" sx={{ fontWeight: 800, lineHeight: 1.4, color: 'white' }}>{ex.title}</Typography>
                            </Box>

                            {/* Generated description */}
                            <Box sx={{ mb: 2.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <AutoIcon sx={{ color: '#8b5cf6', fontSize: 14 }} />
                                    <Typography variant="caption" sx={{ color: '#8b5cf6', fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.1em' }}>AI DESCRIPTION</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.7 }}>{ex.description}</Typography>
                            </Box>

                            {/* Tags */}
                            <Box>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontWeight: 600, display: 'block', mb: 1, fontSize: '0.65rem', letterSpacing: '0.1em' }}>HASHTAGS / TAGS</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {ex.tags.map((tag) => (
                                        <Chip key={tag} label={tag} size="small" sx={{ bgcolor: `${ex.color}12`, color: ex.color, border: `1px solid ${ex.color}30`, fontWeight: 600, fontSize: '0.7rem' }} />
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Text side */}
                    <Box sx={{ flex: 1, minWidth: 0, order: { xs: 1, md: 2 } }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.8, borderRadius: '100px', bgcolor: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.25)', mb: 3 }}>
                            <AutoIcon sx={{ fontSize: 14, color: '#8b5cf6' }} />
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#8b5cf6', letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                                AI SEO WRITER
                            </Typography>
                        </Box>

                        <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2.2rem', md: '3.5rem' }, lineHeight: 1.1, letterSpacing: '-0.04em', mb: 2.5 }}>
                            AI writes your titles &{' '}
                            <Box component="span" sx={{ background: 'linear-gradient(90deg, #8b5cf6, #c084fc)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                                descriptions.
                            </Box>
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.8, fontSize: '1.05rem', mb: 4, maxWidth: 460 }}>
                            Stop spending hours crafting captions and video titles. Our Gemini-powered AI reads your content, understands your audience, and generates platform-optimised titles, descriptions, and hashtags — in seconds.
                        </Typography>

                        <Stack spacing={1.5} sx={{ mb: 4 }}>
                            {[
                                'Platform-specific tone & format',
                                'SEO-optimised for search & discovery',
                                'Hook-first writing that drives clicks',
                                'Hashtag & keyword recommendations',
                            ].map((pt, i) => (
                                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <CheckIcon sx={{ color: '#8b5cf6', fontSize: 18, flexShrink: 0 }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.75)' }}>{pt}</Typography>
                                </Box>
                            ))}
                        </Stack>

                        <Button endIcon={<ArrowForwardIcon />}
                            sx={{ color: '#8b5cf6', fontWeight: 800, fontSize: '1rem', textTransform: 'none', px: 0, '&:hover': { bgcolor: 'transparent', opacity: 0.8 } }}>
                            Try AI writing now
                        </Button>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────────────────────
   MAIN LANDING PAGE
───────────────────────────────────────────────────────────────────────────── */
const LandingPage = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [waitlistMode, setWaitlistMode] = useState(false);

    useEffect(() => {
        featuresApi.getWaitlistMode().then(setWaitlistMode);
    }, []);

    // SALES ORDER: Revenue promise → Data/intelligence → Automation reinforcement
    const features = [
        {
            tag: 'AI AUTOMATION',
            tagColor: '#00d2ff',
            icon: <ReplyIcon sx={{ color: '#00d2ff', fontSize: 18 }} />,
            title: 'Turn every comment into a sales conversion.',
            description: 'Our sentiment-aware AI monitors your posts 24/7, detecting high-intent leads and replying instantly with your unique brand voice — while you sleep.',
            points: ['Real-time high-intent lead detection', 'Brand-synced AI reply personality', 'Automated sales funnel tracking'],
            imageSrc: '/assets/crm_preview.png',
            reverse: false, // text LEFT, image RIGHT
        },
        {
            tag: 'CONTENT INTELLIGENCE',
            tagColor: '#8b5cf6',
            icon: <InsightsIcon sx={{ color: '#8b5cf6', fontSize: 18 }} />,
            title: 'Stop guessing. Start dominating the algorithm.',
            description: "Identify exactly what content your audience wants before you create it. Analyse competitor gaps and let AI tell you what to post, when to post, and how to hook them.",
            points: ['Content Gap Analysis vs competitors', 'Trend & keyword opportunity finder', 'AI-ranked post ideas by potential reach'],
            imageSrc: '/assets/hero_mockup.png',
            reverse: true, // image LEFT, text RIGHT
        },
        {
            tag: 'SMART ENGAGEMENT',
            tagColor: '#10d98a',
            icon: <AutoIcon sx={{ color: '#10d98a', fontSize: 18 }} />,
            title: 'Drive loyal traffic on autopilot.',
            description: 'Transform passive followers into active buyers. Our AI filters noise, prioritises high-value interactions, and keeps your audience engaged 24/7 — no team required.',
            points: ['Sentiment-based comment filtering', 'Dynamic CTA injection at the right moment', 'Full lead attribution & conversion tracking'],
            imageSrc: '/assets/crm_preview.png',
            reverse: false, // text LEFT, image RIGHT
        },
    ];

    return (
        <Box sx={{ bgcolor: '#05060f', minHeight: '100vh', color: 'white', fontFamily: '"Outfit", "Inter", sans-serif', overflowX: 'hidden' }}>

            {/* ── BACKGROUND ── */}
            <Box sx={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', background: `radial-gradient(ellipse 80vw 60vw at 10% 0%, rgba(0,210,255,0.06) 0%, transparent 60%), radial-gradient(ellipse 60vw 60vw at 90% 80%, rgba(139,92,246,0.05) 0%, transparent 60%)` }} />
            <Box sx={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.35, backgroundImage: `linear-gradient(rgba(0,210,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,210,255,0.04) 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />

            {/* ── NAVIGATION ── */}
            <Box component="nav" sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, backdropFilter: 'blur(20px)', bgcolor: 'rgba(5,6,15,0.85)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {/* Main nav row */}
                <Box sx={{ py: 2, px: { xs: 2.5, md: 8 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {/* Logo */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 36, height: 36, borderRadius: 2, background: 'linear-gradient(135deg, #00d2ff 0%, #3a7bd5 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '1.1rem', color: '#000', boxShadow: '0 0 16px rgba(0,210,255,0.4)' }}>S</Box>
                        <Typography variant="h6" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>Socialixer</Typography>
                    </Box>

                    {/* Desktop links */}
                    <Stack direction="row" spacing={4} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}>
                        <MuiLink href="#features" sx={{ color: 'rgba(255,255,255,0.65)', fontWeight: 600, textDecoration: 'none', '&:hover': { color: '#00d2ff' }, transition: 'color 0.2s' }}>Features</MuiLink>
                        <MuiLink href="#demo" sx={{ color: 'rgba(255,255,255,0.65)', fontWeight: 600, textDecoration: 'none', '&:hover': { color: '#00d2ff' }, transition: 'color 0.2s' }}>Demo</MuiLink>
                        {!waitlistMode && <MuiLink href="#pricing" sx={{ color: 'rgba(255,255,255,0.65)', fontWeight: 600, textDecoration: 'none', '&:hover': { color: '#00d2ff' }, transition: 'color 0.2s' }}>Pricing</MuiLink>}
                        <MuiLink href="#faq" sx={{ color: 'rgba(255,255,255,0.65)', fontWeight: 600, textDecoration: 'none', '&:hover': { color: '#00d2ff' }, transition: 'color 0.2s' }}>FAQ</MuiLink>
                        {!waitlistMode && <Button component={Link} to="/app" variant="text" sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 700, px: 2, borderRadius: '100px', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' }, transition: 'all 0.25s' }}>Log In</Button>}
                        <Button href={waitlistMode ? '#waitlist' : undefined} component={waitlistMode ? 'a' : Link} to={waitlistMode ? undefined : '/app'} variant="contained" sx={{ bgcolor: '#fff', color: '#000', fontWeight: 800, px: 3.5, borderRadius: '100px', '&:hover': { bgcolor: '#00d2ff', transform: 'translateY(-2px)' }, transition: 'all 0.25s' }}>
                            {waitlistMode ? 'Join Waitlist' : 'Get Started'}
                        </Button>
                    </Stack>

                    {/* Mobile hamburger */}
                    <IconButton
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        sx={{ display: { xs: 'flex', md: 'none' }, color: 'white', p: 1 }}
                        aria-label="Toggle menu"
                    >
                        {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
                    </IconButton>
                </Box>

                {/* Mobile dropdown */}
                <Collapse in={mobileMenuOpen}>
                    <Box sx={{ px: 2.5, pb: 3, display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                        {[['Features', '#features'], ['Demo', '#demo'], ...(!waitlistMode ? [['Pricing', '#pricing']] : []), ['FAQ', '#faq']].map(([label, href]) => (
                            <MuiLink
                                key={label}
                                href={href}
                                onClick={() => setMobileMenuOpen(false)}
                                sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 600, textDecoration: 'none', py: 1.5, fontSize: '1rem', borderBottom: '1px solid rgba(255,255,255,0.05)', '&:hover': { color: '#00d2ff' }, transition: 'color 0.2s' }}
                            >
                                {label}
                            </MuiLink>
                        ))}
                        {!waitlistMode && (
                            <Button component={Link} to="/app" onClick={() => setMobileMenuOpen(false)} variant="outlined" fullWidth sx={{ mt: 1.5, borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.85)', fontWeight: 700, py: 1.5, borderRadius: '100px', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.05)' } }}>
                                Log In
                            </Button>
                        )}
                        <Button href={waitlistMode ? '#waitlist' : undefined} component={waitlistMode ? 'a' : Link} to={waitlistMode ? undefined : '/app'} onClick={() => setMobileMenuOpen(false)} variant="contained" fullWidth sx={{ mt: 1, bgcolor: '#00d2ff', color: '#000', fontWeight: 800, py: 1.5, borderRadius: '100px', '&:hover': { bgcolor: '#fff' } }}>
                            {waitlistMode ? 'Join Waitlist' : 'Get Started'}
                        </Button>
                    </Box>
                </Collapse>
            </Box>

            {/* ── HERO ── */}
            <Box component="main" role="main">
                <Box component="header">
                    <Box sx={{ pt: { xs: 18, md: 24 }, pb: { xs: 10, md: 16 }, position: 'relative', zIndex: 1 }}>
                        <Container maxWidth="lg">
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: { xs: 8, md: 10 } }}>
                                {/* Text */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2.5, py: 0.8, borderRadius: '100px', mb: 3, bgcolor: 'rgba(0,210,255,0.07)', border: '1px solid rgba(0,210,255,0.2)' }}>
                                        <BoltIcon sx={{ color: '#00d2ff', fontSize: 16 }} />
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#00d2ff', letterSpacing: '0.12em', textTransform: 'uppercase' }}>AI-Powered Social Growth</Typography>
                                    </Box>
                                    <Typography variant="h1" sx={{ fontSize: { xs: '2.8rem', md: '4.8rem' }, fontWeight: 900, lineHeight: 1.05, letterSpacing: '-0.04em', mb: 3, background: 'linear-gradient(160deg, #fff 50%, rgba(255,255,255,0.45) 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                                        Master Social Chaos{' '}
                                        <Box component="span" sx={{ color: '#00d2ff', WebkitTextFillColor: '#00d2ff' }}>with AI.</Box>
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.15rem' }, mb: 4, maxWidth: 480 }}>
                                        The primary AI layer for high-intent social growth. Post to YouTube, Instagram, and Facebook simultaneously, automate engagement, generate captions, and capture revenue — all from one platform.
                                    </Typography>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 5 }}>
                                        {waitlistMode ? (
                                            <Button href="#waitlist" variant="contained" size="large"
                                                sx={{ py: 2, px: 5, fontSize: '1rem', fontWeight: 800, bgcolor: '#00d2ff', color: '#000', borderRadius: '100px', boxShadow: '0 0 30px rgba(0,210,255,0.35)', '&:hover': { bgcolor: '#fff', transform: 'translateY(-3px)' }, transition: 'all 0.3s' }}>
                                                Join the Waitlist
                                            </Button>
                                        ) : (
                                            <Button component={Link} to="/app" variant="contained" size="large"
                                                sx={{ py: 2, px: 5, fontSize: '1rem', fontWeight: 800, bgcolor: '#00d2ff', color: '#000', borderRadius: '100px', boxShadow: '0 0 30px rgba(0,210,255,0.35)', '&:hover': { bgcolor: '#fff', transform: 'translateY(-3px)' }, transition: 'all 0.3s' }}>
                                                Start for Free
                                            </Button>
                                        )}
                                        <Button href="#demo" variant="outlined" size="large" startIcon={<PlayIcon />}
                                            sx={{ py: 2, px: 5, fontSize: '1rem', fontWeight: 700, borderColor: 'rgba(255,255,255,0.2)', color: 'white', borderRadius: '100px', '&:hover': { borderColor: 'rgba(255,255,255,0.7)', bgcolor: 'rgba(255,255,255,0.05)' } }}>
                                            See Demo
                                        </Button>
                                    </Stack>
                                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ gap: 2 }}>
                                        {[
                                            { icon: '🔒', text: 'No credit card required' },
                                            { icon: '⚡', text: 'Setup in 5 minutes' },
                                            { icon: '🤖', text: 'Powered by Gemini AI' },
                                        ].map((badge) => (
                                            <Box key={badge.text} sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                                <Typography sx={{ fontSize: '0.85rem' }}>{badge.icon}</Typography>
                                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', fontWeight: 600, fontSize: '0.8rem' }}>
                                                    {badge.text}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>

                                {/* Hero image */}
                                <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
                                    <Box sx={{ position: 'absolute', inset: '-30%', background: 'radial-gradient(circle, rgba(0,210,255,0.12) 0%, transparent 70%)', zIndex: 0, pointerEvents: 'none' }} />
                                    <Box sx={{ position: 'relative', zIndex: 1, borderRadius: 5, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', bgcolor: 'rgba(255,255,255,0.02)', boxShadow: '0 40px 100px rgba(0,0,0,0.6)', transition: 'transform 0.5s cubic-bezier(0.2,0.8,0.2,1)', '&:hover': { transform: 'translateY(-8px)' } }}>
                                        <Box component="img" src="/assets/hero_mockup.png" alt="Dashboard Preview" sx={{ width: '100%', display: 'block' }}
                                            onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.minHeight = '360px'; e.target.parentElement.style.background = 'linear-gradient(135deg, rgba(0,210,255,0.08) 0%, rgba(139,92,246,0.08) 100%)'; }} />
                                    </Box>
                                </Box>
                            </Box>
                        </Container>
                    </Box>
                </Box>{/* /header */}

                {/* ── STATS ── */}
                <Box sx={{ py: 6, borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', position: 'relative', zIndex: 1 }}>
                    <Container maxWidth="lg">
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-around', alignItems: 'center', gap: { xs: 4, sm: 0 } }}>
                            {[
                                { label: 'Core Platforms', value: '3' },
                                { label: 'Posting Mode', value: 'Simultaneous' },
                                { label: 'Feature Cycle', value: 'Monthly' },
                                { label: 'AI Support', value: '24/7' },
                            ].map((s, i) => (
                                <Box key={i} sx={{ textAlign: 'center' }}>
                                    <Typography variant="h3" sx={{ fontWeight: 900, color: '#00d2ff', letterSpacing: '-0.03em', lineHeight: 1 }}>{s.value}</Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', fontWeight: 600, mt: 0.5 }}>{s.label}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Container>
                </Box>

                {/* ── MONTHLY FEATURES CALLOUT ── */}
                <Box sx={{ py: 3, bgcolor: 'rgba(0,210,255,0.03)', borderBottom: '1px solid rgba(0,210,255,0.1)' }}>
                    <Container maxWidth="lg">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                            <AutoIcon sx={{ color: '#00d2ff', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#00d2ff', letterSpacing: '0.05em' }}>
                                NEW FEATURES LAUNCHING EVERY MONTH — STAY TUNED
                            </Typography>
                        </Box>
                    </Container>
                </Box>

                {/* ── UNIFIED PLATFORM — Hook #1: Biggest pain point ── */}
                <UnifiedPlatformSection />

                {/* ── AI SEO WRITER — Hook #2: Immediate tangible win ── */}
                <AiSeoSection />

                {/* ── ZIGZAG FEATURES — Deep value props ── */}
                <Box id="features" sx={{ py: { xs: 10, md: 14 }, position: 'relative', zIndex: 1 }}>
                    <Container maxWidth="lg">
                        <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 10 } }}>
                            <Typography variant="overline" sx={{ fontWeight: 800, color: '#00d2ff', letterSpacing: '0.18em', fontSize: '0.7rem' }}>FEATURES</Typography>
                            <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '3rem' }, letterSpacing: '-0.04em', mt: 1 }}>
                                Everything you need to dominate.
                            </Typography>
                        </Box>
                        {features.map((f, idx) => <ZigzagSection key={idx} {...f} />)}
                    </Container>
                </Box>

                {/* ── DEMO — Show before final CTA ── */}
                <DemoSection />


                {/* ── PRICING SECTION — hidden in waitlist mode ── */}
                {!waitlistMode && <PricingSection />}

                {/* ── WAITLIST / NEWSLETTER ── */}
                <Box id="waitlist">
                    <NewsletterSection />
                </Box>

                {/* ── FAQ SECTION (GEO signal — mirrors JSON-LD FAQPage schema) ── */}
                <FaqSection />

                {/* ── BOTTOM CTA ── */}
                <Box sx={{ py: { xs: 14, md: 22 }, textAlign: 'center', position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '60vw', height: '30vh', background: 'radial-gradient(circle, rgba(0,210,255,0.15) 0%, transparent 70%)', filter: 'blur(80px)', pointerEvents: 'none' }} />
                    <Container maxWidth="md" sx={{ position: 'relative' }}>
                        <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2.2rem', md: '4.5rem' }, letterSpacing: '-0.04em', lineHeight: 1.05, mb: 3 }}>
                            Take command of your future.
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.1rem', mb: 5, maxWidth: 500, mx: 'auto' }}>
                            Stop the manual grind. Start scaling your brand with AI that works as hard as you do.
                        </Typography>
                        {waitlistMode ? (
                            <Button href="#waitlist" variant="contained" size="large"
                                sx={{ py: 2.5, px: 10, fontSize: '1.2rem', fontWeight: 900, bgcolor: '#00d2ff', color: '#000', borderRadius: '100px', boxShadow: '0 0 60px rgba(0,210,255,0.4)', '&:hover': { bgcolor: '#fff', transform: 'scale(1.05)' }, transition: 'all 0.3s' }}>
                                Join the Waitlist
                            </Button>
                        ) : (
                            <Button component={Link} to="/app" variant="contained" size="large"
                                sx={{ py: 2.5, px: 10, fontSize: '1.2rem', fontWeight: 900, bgcolor: '#00d2ff', color: '#000', borderRadius: '100px', boxShadow: '0 0 60px rgba(0,210,255,0.4)', '&:hover': { bgcolor: '#fff', transform: 'scale(1.05)' }, transition: 'all 0.3s' }}>
                                Get Started Now
                            </Button>
                        )}
                    </Container>
                </Box>
            </Box>{/* /main */}

            {/* ── FOOTER ── */}
            <Box component="footer" sx={{ py: 10, borderTop: '1px solid rgba(255,255,255,0.05)', position: 'relative', zIndex: 1 }}>
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 6, mb: 6 }}>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                <Box sx={{ width: 32, height: 32, borderRadius: 2, background: 'linear-gradient(135deg, #00d2ff 0%, #3a7bd5 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, color: '#000' }}>S</Box>
                                <Typography variant="h6" sx={{ fontWeight: 900 }}>Socialixer</Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.38)', maxWidth: 280, lineHeight: 1.7 }}>
                                Building the infrastructure for the next generation of social creators.
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: { xs: 6, md: 10 } }}>
                            {[
                                { heading: 'Platform', links: ['AI Growth', 'Lead CRM', 'Automation', 'AI Writer'] },
                                { heading: 'Legal', links: [['Terms', '/terms'], ['Privacy', '/privacy']] },
                            ].map((col) => (
                                <Stack key={col.heading} spacing={2}>
                                    <Typography variant="overline" sx={{ fontWeight: 900, color: 'white', letterSpacing: '0.12em', fontSize: '0.65rem' }}>{col.heading}</Typography>
                                    {col.links.map((l) =>
                                        Array.isArray(l) ? (
                                            <MuiLink key={l[0]} component={Link} to={l[1]} sx={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none', fontSize: '0.9rem', '&:hover': { color: '#00d2ff' }, transition: 'color 0.2s' }}>{l[0]}</MuiLink>
                                        ) : (
                                            <MuiLink key={l} sx={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none', cursor: 'pointer', fontSize: '0.9rem', '&:hover': { color: '#00d2ff' }, transition: 'color 0.2s' }}>{l}</MuiLink>
                                        )
                                    )}
                                </Stack>
                            ))}
                        </Box>
                    </Box>
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', mb: 4 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.25)', textAlign: 'center' }}>
                        © {new Date().getFullYear()} Socialixer. All rights reserved.
                    </Typography>
                </Container>
            </Box>
        </Box>
    );
};

export default LandingPage;

/* ─────────────────────────────────────────────────────────────────────────────
   FAQ SECTION
   Mirrors the JSON-LD FAQPage schema in index.html.
   Visible Q&A text is the primary GEO signal — AI engines parse it the same
   way they parse web content, so entity-rich, direct answers here help
   Socialixer get cited in AI-generated answers (ChatGPT, Perplexity, SGE).
───────────────────────────────────────────────────────────────────────────── */
function FaqSection() {
    const [open, setOpen] = useState(null);
    const toggle = (i) => setOpen(open === i ? null : i);

    const faqs = [
        {
            q: 'What is Socialixer and what does it do?',
            a: 'Socialixer is an AI-powered social media automation platform. It lets creators and brands schedule posts, generate AI content ideas (powered by Google Gemini), and manage social media leads — all from a single dashboard. It currently supports YouTube, Instagram, and Facebook with simultaneous posting, with more platforms coming soon.',
        },
        {
            q: 'How does Socialixer generate content ideas with AI?',
            a: 'Enter a topic and optionally select a target platform. Socialixer sends this to Google Gemini 1.5 Flash, which returns structured ideas: each idea includes a title, hook (opening line), full post body, call-to-action, recommended format (Short Post / Thread / Story / Reel / Carousel / Poll), engagement prediction (High / Medium / Low), and relevant hashtags. If AI is unavailable, a heuristic template library provides instant fallback ideas.',
        },
        {
            q: 'Which social media platforms does Socialixer support?',
            a: 'Socialixer currently focuses on three core platforms: YouTube (video uploads via YouTube Data API v3), Instagram (feed posts and Reels via Graph API), and Facebook (page posts via Graph API). We are expanding to support Twitter/X, LinkedIn, and others in future monthly updates.',
        },
        {
            q: 'What is content gap analysis in Socialixer?',
            a: "Content gap analysis compares your social media post against a competitor's post or URL. Socialixer analyses both for keyword frequency, topic coverage, sentiment, readability (Flesch reading ease), and engagement signals (questions, emojis, hashtags, CTAs, numbers). It scores the gap and uses Gemini AI to suggest 5 rewritten versions of your post that close the identified gaps.",
        },
        {
            q: 'Is Socialixer free? Do I need a credit card?',
            a: "Socialixer is free and self-hostable — no credit card required. You bring your own API credentials for each social platform (Instagram, Twitter, LinkedIn, etc.) and your own AI API key (Google Gemini is free-tier eligible). There is no per-seat or per-post cost from Socialixer itself.",
        },
        {
            q: 'How does Socialixer handle Instagram engagement automation?',
            a: "Socialixer uses Instagram's Webhooks API to receive real-time notifications whenever someone comments on your posts. An AI intent classifier categorises each comment as a lead, question, spam, positive, or negative signal. If auto-reply is enabled and the comment passes all safety filters, Socialixer generates a contextually relevant reply and posts it via the Graph API. Processed comment IDs are persisted in Firestore, so duplicate replies are prevented even across server restarts.",
        },
        {
            q: 'Are my social media API credentials stored securely?',
            a: 'Yes. All API credentials are encrypted with Fernet (AES-128-CBC + HMAC-SHA256 authentication) before being written to Firebase Firestore. The Fernet encryption key lives exclusively in the server-side environment variable CREDENTIAL_ENCRYPTION_KEY and is never stored in the database or sent to the browser. Credentials are decrypted server-side on demand and values are masked (shown as ••••••••) in the frontend Settings UI.',
        },
    ];

    return (
        <Box
            id="faq"
            component="section"
            aria-labelledby="faq-heading"
            sx={{ py: { xs: 10, md: 14 }, position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}
        >
            {/* Glow */}
            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '60vw', height: '40vh', background: 'radial-gradient(circle, rgba(139,92,246,0.07) 0%, transparent 70%)', filter: 'blur(80px)', pointerEvents: 'none' }} />

            <Container maxWidth="md" sx={{ position: 'relative' }}>
                <Box sx={{ textAlign: 'center', mb: 8 }}>
                    <Typography variant="overline" sx={{ fontWeight: 800, color: '#8b5cf6', letterSpacing: '0.18em', fontSize: '0.7rem' }}>
                        FREQUENTLY ASKED
                    </Typography>
                    <Typography
                        id="faq-heading"
                        variant="h2"
                        sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '3rem' }, letterSpacing: '-0.04em', mt: 1, mb: 2 }}
                    >
                        Everything you need to know.
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.45)', maxWidth: 480, mx: 'auto', lineHeight: 1.8 }}>
                        Quick answers to the most common questions about Socialixer.
                    </Typography>
                </Box>

                <Box component="dl" sx={{ m: 0, p: 0 }}>
                    {faqs.map((faq, i) => (
                        <Box
                            key={i}
                            sx={{
                                mb: 2,
                                borderRadius: 3,
                                border: open === i ? '1px solid rgba(139,92,246,0.35)' : '1px solid rgba(255,255,255,0.07)',
                                bgcolor: open === i ? 'rgba(139,92,246,0.06)' : 'rgba(255,255,255,0.025)',
                                transition: 'all 0.25s',
                                overflow: 'hidden',
                                '&:hover': { borderColor: 'rgba(139,92,246,0.25)', bgcolor: 'rgba(139,92,246,0.04)' },
                            }}
                        >
                            {/* Question */}
                            <Box
                                component="dt"
                                role="button"
                                tabIndex={0}
                                aria-expanded={open === i}
                                onClick={() => toggle(i)}
                                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggle(i)}
                                sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    px: 3, py: 2.5, cursor: 'pointer', gap: 2, m: 0,
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', md: '1rem' }, lineHeight: 1.5 }}
                                >
                                    {faq.q}
                                </Typography>
                                <Box sx={{ color: open === i ? '#8b5cf6' : 'rgba(255,255,255,0.4)', flexShrink: 0, display: 'flex', transition: 'color 0.2s' }}>
                                    {open === i ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </Box>
                            </Box>

                            {/* Answer */}
                            <Collapse in={open === i}>
                                <Box
                                    component="dd"
                                    sx={{ px: 3, pb: 3, pt: 0, m: 0 }}
                                >
                                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 2.5 }} />
                                    <Typography
                                        variant="body2"
                                        sx={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.85, fontSize: '0.95rem' }}
                                    >
                                        {faq.a}
                                    </Typography>
                                </Box>
                            </Collapse>
                        </Box>
                    ))}
                </Box>

            </Container>
        </Box>
    );
}
